/**
 * PDF Statement Export Service for habitué
 * Generates monthly statements with transaction history
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// habitué brand colors
const COLORS = {
  primary: '#0F1A14',
  secondary: '#1E2F26',
  gold: '#C3A365',
  text: '#E8EBE9',
  muted: '#8BA89A'
};

/**
 * Generate a PDF statement for a user's transactions
 * @param {Object} options Configuration options
 * @param {Object} options.user User information
 * @param {Array} options.transactions List of transactions
 * @param {string} options.period Period string (e.g., "December 2025")
 * @param {Object} options.summary Summary data (opening/closing balance, etc.)
 * @param {string} options.language 'fr' or 'en'
 */
export const generateStatement = ({
  user,
  transactions = [],
  period,
  summary = {},
  language = 'fr'
}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Translations
  const t = {
    fr: {
      title: 'Relevé Mensuel',
      accountStatement: 'Relevé de Compte',
      member: 'Membre',
      accountNumber: 'Numéro de compte',
      period: 'Période',
      generatedOn: 'Généré le',
      summary: 'Résumé',
      openingBalance: 'Solde d\'ouverture',
      totalCredits: 'Total crédits',
      totalDebits: 'Total débits',
      closingBalance: 'Solde de clôture',
      pointsEarned: 'Points gagnés',
      transactions: 'Transactions',
      date: 'Date',
      description: 'Description',
      amount: 'Montant',
      balance: 'Solde',
      type: 'Type',
      noTransactions: 'Aucune transaction pour cette période',
      footer: 'Ce document est généré automatiquement par habitué.',
      contact: 'Contact: support@habitue.com',
      page: 'Page',
      of: 'sur',
      credit: 'Crédit',
      debit: 'Débit',
      topup: 'Recharge',
      payment: 'Paiement',
      giftReceived: 'Carte cadeau reçue',
      giftSent: 'Carte cadeau envoyée',
      pointsRedeemed: 'Points convertis',
      refund: 'Remboursement'
    },
    en: {
      title: 'Monthly Statement',
      accountStatement: 'Account Statement',
      member: 'Member',
      accountNumber: 'Account number',
      period: 'Period',
      generatedOn: 'Generated on',
      summary: 'Summary',
      openingBalance: 'Opening balance',
      totalCredits: 'Total credits',
      totalDebits: 'Total debits',
      closingBalance: 'Closing balance',
      pointsEarned: 'Points earned',
      transactions: 'Transactions',
      date: 'Date',
      description: 'Description',
      amount: 'Amount',
      balance: 'Balance',
      type: 'Type',
      noTransactions: 'No transactions for this period',
      footer: 'This document is automatically generated by habitué.',
      contact: 'Contact: support@habitue.com',
      page: 'Page',
      of: 'of',
      credit: 'Credit',
      debit: 'Debit',
      topup: 'Top-up',
      payment: 'Payment',
      giftReceived: 'Gift card received',
      giftSent: 'Gift card sent',
      pointsRedeemed: 'Points redeemed',
      refund: 'Refund'
    }
  }[language];

  // Helper to format currency
  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toFixed(2)}`;
  
  // Helper to format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get transaction type label
  const getTypeLabel = (type) => {
    const labels = {
      topup: t.topup,
      payment: t.payment,
      gift_received: t.giftReceived,
      gift_sent: t.giftSent,
      points_redeemed: t.pointsRedeemed,
      refund: t.refund
    };
    return labels[type] || type;
  };

  // ============ HEADER ============
  // Dark header background
  doc.setFillColor(15, 26, 20); // #0F1A14
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Logo text
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(195, 163, 101); // Gold
  doc.text('habitué', 20, 25);
  
  // Statement title
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(232, 235, 233); // Light text
  doc.text(t.accountStatement.toUpperCase(), pageWidth - 20, 20, { align: 'right' });
  
  // Period
  doc.setFontSize(10);
  doc.setTextColor(139, 168, 154); // Muted
  doc.text(period, pageWidth - 20, 30, { align: 'right' });

  // ============ USER INFO ============
  let yPos = 60;
  
  doc.setFontSize(10);
  doc.setTextColor(139, 168, 154);
  doc.text(t.member, 20, yPos);
  doc.setTextColor(30, 47, 38);
  doc.setFont('helvetica', 'bold');
  doc.text(user.name || 'Membre habitué', 70, yPos);
  
  yPos += 8;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(139, 168, 154);
  doc.text(t.accountNumber, 20, yPos);
  doc.setTextColor(30, 47, 38);
  doc.text(`HAB-${(user.id || '000000').slice(-6).toUpperCase()}`, 70, yPos);
  
  yPos += 8;
  doc.setTextColor(139, 168, 154);
  doc.text(t.generatedOn, 20, yPos);
  doc.setTextColor(30, 47, 38);
  doc.text(formatDate(new Date().toISOString()), 70, yPos);

  // ============ SUMMARY BOX ============
  yPos += 15;
  
  // Summary background
  doc.setFillColor(30, 47, 38); // #1E2F26
  doc.roundedRect(20, yPos, pageWidth - 40, 50, 3, 3, 'F');
  
  // Summary title
  doc.setFontSize(11);
  doc.setTextColor(195, 163, 101);
  doc.text(t.summary.toUpperCase(), 30, yPos + 12);
  
  // Summary grid
  const summaryY = yPos + 25;
  const colWidth = (pageWidth - 60) / 4;
  
  const summaryData = [
    { label: t.openingBalance, value: formatCurrency(summary.openingBalance || 0) },
    { label: t.totalCredits, value: `+${formatCurrency(summary.totalCredits || 0)}` },
    { label: t.totalDebits, value: `-${formatCurrency(summary.totalDebits || 0)}` },
    { label: t.closingBalance, value: formatCurrency(summary.closingBalance || 0) }
  ];
  
  summaryData.forEach((item, i) => {
    const x = 30 + (i * colWidth);
    doc.setFontSize(8);
    doc.setTextColor(139, 168, 154);
    doc.text(item.label, x, summaryY);
    doc.setFontSize(12);
    doc.setTextColor(232, 235, 233);
    doc.setFont('helvetica', 'bold');
    doc.text(item.value, x, summaryY + 10);
    doc.setFont('helvetica', 'normal');
  });
  
  // Points earned
  yPos += 55;
  doc.setFontSize(9);
  doc.setTextColor(195, 163, 101);
  doc.text(`${t.pointsEarned}: ${summary.pointsEarned || 0} pts`, 20, yPos);

  // ============ TRANSACTIONS TABLE ============
  yPos += 15;
  
  doc.setFontSize(11);
  doc.setTextColor(30, 47, 38);
  doc.setFont('helvetica', 'bold');
  doc.text(t.transactions, 20, yPos);
  doc.setFont('helvetica', 'normal');
  
  yPos += 5;
  
  if (transactions.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(139, 168, 154);
    doc.text(t.noTransactions, 20, yPos + 10);
  } else {
    // Prepare table data
    const tableData = transactions.map(tx => [
      formatDate(tx.date || tx.created_at),
      getTypeLabel(tx.type),
      tx.description || tx.merchant_name || '-',
      tx.amount >= 0 ? `+${formatCurrency(tx.amount)}` : formatCurrency(tx.amount),
      formatCurrency(tx.balance_after || 0)
    ]);
    
    // Generate table using functional API (jspdf-autotable v5+)
    autoTable(doc, {
      startY: yPos,
      head: [[t.date, t.type, t.description, t.amount, t.balance]],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [30, 47, 38],
        textColor: [195, 163, 101],
        fontSize: 9,
        fontStyle: 'bold',
        cellPadding: 4
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 4,
        textColor: [30, 47, 38]
      },
      alternateRowStyles: {
        fillColor: [245, 247, 246]
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 25, halign: 'right' }
      },
      margin: { left: 20, right: 20 },
      didDrawCell: (data) => {
        // Color positive amounts green, negative red
        if (data.column.index === 3 && data.cell.section === 'body') {
          const text = data.cell.text?.[0] || '';
          if (text.startsWith('+')) {
            doc.setTextColor(76, 175, 80);
          } else if (text.startsWith('-') || text.startsWith('£-')) {
            doc.setTextColor(244, 67, 54);
          }
        }
      }
    });
  }

  // ============ FOOTER ============
  const footerY = pageHeight - 20;
  
  // Footer line
  doc.setDrawColor(195, 163, 101);
  doc.setLineWidth(0.5);
  doc.line(20, footerY - 10, pageWidth - 20, footerY - 10);
  
  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(139, 168, 154);
  doc.text(t.footer, 20, footerY - 3);
  doc.text(t.contact, 20, footerY + 3);
  
  // Page number
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(139, 168, 154);
    doc.text(
      `${t.page} ${i} ${t.of} ${pageCount}`,
      pageWidth - 20,
      footerY,
      { align: 'right' }
    );
  }

  // Generate filename
  const safePeriod = period.replace(/\s+/g, '-').toLowerCase();
  const filename = `habitue-statement-${safePeriod}.pdf`;
  
  // Save the PDF
  doc.save(filename);
  
  return filename;
};

/**
 * Generate a simple transaction receipt
 */
export const generateReceipt = ({ transaction, user, language = 'fr' }) => {
  const doc = new jsPDF({
    format: [80, 150], // Receipt size
    unit: 'mm'
  });
  
  const t = language === 'fr' ? {
    receipt: 'Reçu',
    date: 'Date',
    amount: 'Montant',
    type: 'Type',
    member: 'Membre',
    thankYou: 'Merci de votre confiance'
  } : {
    receipt: 'Receipt',
    date: 'Date',
    amount: 'Amount',
    type: 'Type',
    member: 'Member',
    thankYou: 'Thank you for your trust'
  };
  
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 10;
  
  // Logo
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(16);
  doc.text('habitué', pageWidth / 2, y, { align: 'center' });
  
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(t.receipt, pageWidth / 2, y, { align: 'center' });
  
  y += 10;
  doc.setFontSize(8);
  doc.text(`${t.date}: ${new Date(transaction.date).toLocaleDateString()}`, 5, y);
  
  y += 6;
  doc.text(`${t.type}: ${transaction.type}`, 5, y);
  
  y += 6;
  doc.text(`${t.member}: ${user.name}`, 5, y);
  
  y += 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`£${transaction.amount.toFixed(2)}`, pageWidth / 2, y, { align: 'center' });
  
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(t.thankYou, pageWidth / 2, y, { align: 'center' });
  
  doc.save(`habitue-receipt-${transaction.id || Date.now()}.pdf`);
};

export default { generateStatement, generateReceipt };
